import { useMemo, useState } from 'react';
import type { CanonicalExam } from '../../core/model/canonical.ts';
import { brandAs, type IsoDateTime, type QuestionId } from '../../core/model/brands.ts';
import { questionAt } from '../../core/model/canonical.ts';
import type { SessionState } from '../../core/exam-engine/state.ts';
import type { SessionEvent } from '../../core/exam-engine/reducer.ts';
import { summarise, toSessionResultsJson } from '../../core/export/sessionResults.ts';
import { notesFilename, toNotesMarkdown } from '../../core/export/notes.ts';
import { notedQuestions } from '../../core/exam-engine/selectors.ts';
import { sourceNumber } from '../../core/exam-engine/blueprint.ts';
import type { Breakdown } from '../../core/statistics/summary.ts';
import { QUESTION_RENDERERS } from '../../questions/registry.tsx';
import { QuestionPrompt } from '../../questions/shared/QuestionParts.tsx';
import { QuestionNote } from '../session/QuestionNote.tsx';
import { CorrectQuestion } from '../review/CorrectQuestion.tsx';
import { isEmptyPatch, toReviewedExamJson, type ExamPatch, type QuestionPatch } from '../../core/export/reviewedExam.ts';
import { useI18n } from '../../i18n/index.tsx';
import { Button, Callout, ExamText } from '../../ui/primitives.tsx';
import { downloadJson, downloadText } from '../../utils/download.ts';
import { APP_VERSION } from '../../app/version.ts';

function formatDuration(ms: number): string {
  const total = Math.round(ms / 1000);
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${String(minutes)}:${String(seconds).padStart(2, '0')}`;
}

/**
 * What happened, and why.
 *
 * The per-area breakdowns are ordered weakest first, because the only actionable thing in a result
 * is where to study next. The headline percentage counts points rather than questions, and leaves
 * out anything the app could not mark — an essay it cannot grade must not read as a mistake.
 */
export function ResultsScreen({
  exam,
  session,
  patch,
  onReview,
  onRestart,
  onGoToQuestion,
}: {
  readonly exam: CanonicalExam;
  readonly session: SessionState;
  readonly patch: ExamPatch;
  readonly onReview: () => void;
  readonly onRestart: () => void;
  readonly onGoToQuestion: (index: number) => void;
}) {
  const { t } = useI18n();
  // A finished session has every segment closed, so the durations no longer depend on "now" —
  // which is exactly why the results screen must not read a clock while rendering.
  const at = finalMoment(session);
  const summary = useMemo(() => summarise(exam, session, at), [exam, session, at]);

  return (
    <section className="screen screen--results" aria-labelledby="results-title">
      <h1 id="results-title">{t('results.title')}</h1>

      <div className="score">
        <p className="score__value">{t('results.score', { percent: summary.scorePercent })}</p>
        <p className="score__detail">
          {t('results.scoreOf', { raw: summary.rawScore, max: summary.maxScore })}
        </p>
        {summary.passed !== undefined && (
          <p className={`score__verdict score__verdict--${summary.passed ? 'pass' : 'fail'}`}>
            {summary.passed ? t('results.passed') : t('results.failed')}
          </p>
        )}
      </div>

      <dl className="stats">
        <Stat label={t('results.correct')} value={summary.correct} />
        <Stat label={t('results.incorrect')} value={summary.incorrect} />
        {summary.partial > 0 && <Stat label={t('results.partial')} value={summary.partial} />}
        <Stat label={t('results.unanswered')} value={summary.unanswered} />
        {summary.ungraded > 0 && <Stat label={t('results.ungraded')} value={summary.ungraded} />}
        <Stat label={t('results.duration')} value={formatDuration(summary.durationMs)} />
        <Stat
          label={t('results.averagePerQuestion')}
          value={formatDuration(summary.averageMsPerQuestion)}
        />
        <Stat label={t('results.markedCount')} value={summary.markedForReviewCount} />
      </dl>

      {summary.ungraded > 0 && (
        <Callout tone="info" title={t('results.ungraded')}>
          <p>{t('results.ungradedHelp')}</p>
        </Callout>
      )}

      <BreakdownTable title={t('results.byCategory')} rows={summary.byCategory} />
      {/* Difficulty and type are enum values, not authored labels, so the interface names them. */}
      <BreakdownTable
        title={t('results.byDifficulty')}
        rows={summary.byDifficulty}
        translate={(id) => t(`difficulty.${id}` as 'difficulty.easy')}
      />
      <BreakdownTable
        title={t('results.byType')}
        rows={summary.byType}
        translate={(id) => t(`type.${id}` as 'type.text')}
      />
      <BreakdownTable title={t('results.byTag')} rows={summary.byTag} />

      <NotesSummary exam={exam} session={session} onGoToQuestion={onGoToQuestion} />

      <div className="actions">
        <Button variant="primary" onClick={onReview}>
          {t('results.review')}
        </Button>
        <Button
          onClick={() => {
            downloadJson(
              toSessionResultsJson(exam, session, at, APP_VERSION),
              'session-results.json',
            );
          }}
        >
          {t('results.export')}
        </Button>
        {/* Only once there is a correction to carry: a "corrected copy" identical to the original
            would be a claim with nothing behind it. */}
        {!isEmptyPatch(patch) && (
          <Button
            onClick={() => {
              downloadJson(
                toReviewedExamJson(exam, patch, {
                  at: brandAs<IsoDateTime>(new Date().toISOString()),
                  producer: `FreExam Player ${APP_VERSION}`,
                }),
                'exam-reviewed.json',
              );
            }}
          >
            {t('correct.export')}
          </Button>
        )}

        <Button onClick={onRestart}>{t('results.newSession')}</Button>
      </div>

      {!isEmptyPatch(patch) && <p className="field__help">{t('correct.exportHelp')}</p>}

      <p className="privacy" role="note">
        {t('app.privacy')}
      </p>
    </section>
  );
}

/**
 * The instant a finished session stopped.
 *
 * Every segment of a completed session is closed, so any value would give the same durations. Using
 * the recorded end rather than `performance.now()` keeps this a pure function of the session, and
 * keeps the results identical however long the screen stays open.
 */
function finalMoment(session: SessionState): number {
  const last = session.clock.segments.at(-1);
  return last?.endedAt ?? last?.startedAt ?? 0;
}

/**
 * Everything the candidate wrote down, gathered in one place.
 *
 * This is what the notes were for. During the exam a note is scattered across whichever question
 * prompted it; the moment it becomes useful is here, read end to end, next to the score that says
 * which of them mattered.
 *
 * Each note links back to its question, because "I was confused about the second one" is only
 * actionable if the second one is one click away.
 */
function NotesSummary({
  exam,
  session,
  onGoToQuestion,
}: {
  readonly exam: CanonicalExam;
  readonly session: SessionState;
  readonly onGoToQuestion: (index: number) => void;
}) {
  const { t } = useI18n();
  const [withQuestions, setWithQuestions] = useState(false);
  const noted = notedQuestions(session);

  if (noted.length === 0) {
    return null;
  }

  return (
    <section className="notes" aria-labelledby="notes-title">
      <h2 id="notes-title">{t('note.summaryTitle', { count: noted.length })}</h2>

      <ol className="notes__list">
        {noted.map((item) => (
          <li key={item.questionId} className="notes__item">
            <p className="notes__meta">
              <button
                type="button"
                className="notes__jump"
                onClick={() => {
                  onGoToQuestion(item.position - 1);
                }}
              >
                {t('session.question', {
                  position: item.position,
                  total: session.blueprint.questionOrder.length,
                })}
              </button>
              <span className="notes__file">
                {t('session.sourceNumber', { number: sourceNumber(exam, item.questionId) })}
              </span>
              {item.markedForReview && (
                <span className="notes__marked">{t('session.status.marked')}</span>
              )}
            </p>
            {/* The candidate's own words, rendered as text. Newlines are kept by the stylesheet. */}
            <p className="notes__text">{item.note}</p>
          </li>
        ))}
      </ol>

      {/* Two files, one choice. The default carries no exam content, which is the safer file to
          have on a disk and to send to someone; including the questions makes it something you can
          revise from without the exam file beside you, and the help text says which you are
          getting. */}
      <label className="checkbox">
        <input
          type="checkbox"
          checked={withQuestions}
          onChange={(event) => {
            setWithQuestions(event.target.checked);
          }}
        />
        <span>{t('note.includeQuestions')}</span>
      </label>

      <p className="field__help">
        {withQuestions ? t('note.exportHelpWithQuestions') : t('note.exportHelp')}
      </p>

      <div className="actions actions--tight">
        <Button
          onClick={() => {
            const options = { includeQuestions: withQuestions };
            downloadText(toNotesMarkdown(exam, session, options), notesFilename(exam, options));
          }}
        >
          {t('note.export')}
        </Button>
      </div>
    </section>
  );
}

function Stat({ label, value }: { readonly label: string; readonly value: string | number }) {
  return (
    <div className="stat">
      <dt>{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}

function BreakdownTable({
  title,
  rows,
  translate,
}: {
  readonly title: string;
  readonly rows: readonly Breakdown[];
  readonly translate?: (id: string) => string;
}) {
  const { t } = useI18n();
  if (rows.length === 0) {
    return null;
  }

  return (
    <section className="breakdown">
      <h2>{title}</h2>
      <div className="breakdown__scroll">
      <table>
        <thead>
          <tr>
            {/* The section heading already names this column; repeating it visually is noise, but a
                screen reader still needs it to announce the row headers. */}
            <th scope="col">
              <span className="visually-hidden">{title}</span>
            </th>
            <th scope="col">{t('results.correct')}</th>
            <th scope="col">%</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id}>
              <th scope="row">{translate === undefined ? row.label : translate(row.id)}</th>
              <td>
                {row.correct} / {row.total}
              </td>
              <td>
                <span className="breakdown__bar" aria-hidden="true">
                  <span className="breakdown__fill" style={{ inlineSize: `${String(row.percent)}%` }} />
                </span>
                {row.percent}%
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    </section>
  );
}

/**
 * Going back through the answers.
 *
 * The same renderers as the session, in a read-only, revealed state — so what a candidate sees
 * afterwards is literally the question they answered, not a second rendering of it that could drift.
 */
export function ReviewScreen({
  exam,
  session,
  startAt,
  patches,
  onPatch,
  onDispatch,
  onBack,
}: {
  readonly exam: CanonicalExam;
  readonly session: SessionState;
  /** Which question to open on. Set when review was entered from a note rather than from the top. */
  readonly startAt: number;
  readonly patches: Readonly<Record<string, QuestionPatch>>;
  readonly onPatch: (questionId: QuestionId, patch: QuestionPatch | undefined) => void;
  readonly onDispatch: (event: SessionEvent) => void;
  readonly onBack: () => void;
}) {
  const { t } = useI18n();
  const [index, setIndex] = useState(startAt);

  const questionId = session.blueprint.questionOrder[index];
  const question = questionId === undefined ? undefined : questionAt(exam, questionId);
  const entry = questionId === undefined ? undefined : session.entries[questionId];
  const score = questionId === undefined ? undefined : session.scores[questionId];

  if (question === undefined || questionId === undefined || entry === undefined) {
    return null;
  }

  const Renderer = QUESTION_RENDERERS[question.type];
  const total = session.blueprint.questionOrder.length;

  return (
    <section className="screen screen--review" aria-labelledby="review-title">
      <h1 id="review-title">{t('session.question', { position: index + 1, total })}</h1>

      <p className={`verdict verdict--${score?.outcome ?? 'unanswered'}`}>
        {score?.outcome === 'correct' && t('session.correct')}
        {score?.outcome === 'incorrect' && t('session.incorrect')}
        {score?.outcome === 'unanswered' && t('results.noAnswer')}
        {score?.outcome === 'ungraded' && t('results.ungraded')}
        {score?.outcome === 'partial' && t('results.partial')}
      </p>

      <article className="question">
        <QuestionPrompt question={question} />
        <Renderer
          question={question}
          optionOrder={session.blueprint.optionOrder[questionId]}
          response={entry.response}
          onChange={() => {
            /* read-only */
          }}
          disabled
          revealed
        />

        {/* Still editable here. Half the value of a note is what you add once you have seen
            whether you were right. */}
        <QuestionNote
          key={questionId}
          note={entry.note}
          onChange={(text) => {
            onDispatch({ type: 'SET_NOTE', questionId, text });
          }}
        />

        {/* The other end of "report a problem": what it should say instead. */}
        <CorrectQuestion
          key={questionId}
          question={question}
          patch={patches[questionId]}
          onChange={(patch) => {
            onPatch(questionId, patch);
          }}
        />

        {question.explanation !== undefined && (
          <section className="explanation">
            <h2>{t('session.explanation')}</h2>
            {question.explanation.summary !== undefined && (
              <ExamText lang={question.content.language}>{question.explanation.summary}</ExamText>
            )}
            {question.explanation.detail !== undefined && (
              <ExamText lang={question.content.language}>{question.explanation.detail}</ExamText>
            )}
          </section>
        )}

        {question.references.length > 0 && (
          <section className="references">
            <h2>{t('session.references')}</h2>
            <ul>
              {question.references.map((reference) => (
                <li key={reference.title}>
                  {reference.url === undefined ? (
                    reference.title
                  ) : (
                    // Untrusted link: no referrer, no opener, and not treated as an endorsement.
                    <a href={reference.url} target="_blank" rel="noopener noreferrer nofollow">
                      {reference.title}
                    </a>
                  )}
                </li>
              ))}
            </ul>
          </section>
        )}

        {score?.outcome === 'ungraded' && (
          <div className="self-grade">
            <p>{t('results.ungradedHelp')}</p>
            <div className="actions">
              <Button
                onClick={() => {
                  onDispatch({ type: 'SELF_GRADE', questionId, correct: true });
                }}
              >
                {t('results.markCorrect')}
              </Button>
              <Button
                onClick={() => {
                  onDispatch({ type: 'SELF_GRADE', questionId, correct: false });
                }}
              >
                {t('results.markIncorrect')}
              </Button>
            </div>
          </div>
        )}
      </article>

      <div className="session__controls">
        <Button
          disabled={index === 0}
          onClick={() => {
            setIndex((current) => current - 1);
          }}
        >
          {t('session.previous')}
        </Button>
        <Button
          disabled={index >= total - 1}
          onClick={() => {
            setIndex((current) => current + 1);
          }}
        >
          {t('session.next')}
        </Button>
        <Button variant="quiet" onClick={onBack}>
          {t('results.title')}
        </Button>
      </div>
    </section>
  );
}

